import { isEqual } from "es-toolkit";
import { makeAutoObservable, runInAction } from "mobx";

import { getUndoManager } from "@/components/tiptap/Collaboration";
import { PhantomHintsModel } from "@/models/PhantomHintsModel";
import { SuggestionsModel } from "@/models/SuggestionsModel";
import { watchClipboardReadable } from "@/utils/watchClipboardReadable";

import type { GraphNodeAttrs } from "@/components/tiptap/Graph";
import type { LatexPair } from "@/components/tiptap/latex-input/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FontSize, Color } from "@corca/data";
import type {
  Alignment,
  CodeBlockAttrs,
  EmbedNodeAttrs,
  PlotlyChartNodeAttrs,
} from "@corca/tiptap";
import type { Editor as TiptapEditor } from "@tiptap/core";

export type EditorState = {
  isDocumentTitle?: boolean;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  isStrikethrough?: boolean;
  isSubscript?: boolean;
  isSuperscript?: boolean;
  isInlineCode?: boolean;
  isCodeBlock?: boolean;
  isCode?: boolean;
  isLink?: boolean;
  linkHref?: string;
  isTextSelected?: boolean;
  isParagraph?: boolean;
  isParagraphNode?: boolean;
  isHeading?: boolean;
  isH1?: boolean;
  isH2?: boolean;
  isH3?: boolean;
  isList?: boolean;
  isBulletList?: boolean;
  isOrderedList?: boolean;
  isTaskList?: boolean;
  isBlockquote?: boolean;
  isCallout?: boolean;
  isTable?: boolean;
  isTableCell?: boolean;
  isTableHeader?: boolean;
  isTableCellLike?: boolean;
  /**
   * This flag means that if you under that block and you trying to convert it into another
   * it won't be converted but new block will be added after current block
   */
  isNonConvertableBlock?: boolean;
  isExpression?: boolean;
  isText?: boolean;
  isImage?: boolean;
  isGraph?: boolean;
  isEmbed?: boolean;
  isImageLoaded?: boolean;
  isHorizontalRule?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  embedAttrs?: EmbedNodeAttrs | null;
  textColor?: Color;
  textSize?: FontSize;
  blockAlign?: Alignment;
  blockBackgroundColor?: Color | null;
  hasMathIndentation?: boolean;
  lineCount?: number;
  graphAttrs?: GraphNodeAttrs | null;
  isPlotlyChart?: boolean;
  plotlyChartAttrs?: PlotlyChartNodeAttrs | null;
  codeBlockAttrs?: CodeBlockAttrs | null;
  /**
   * The LaTeX input state when typing LaTeX expressions (started with $ or $$).
   * Null when not in LaTeX input mode.
   */
  latexInput?: LatexPair | null;
};

export class EditorModel {
  doc: DocumentModel;

  tiptap?: TiptapEditor = undefined;

  editorState: Partial<EditorState> = {};

  isLinkEditMode: boolean = false;

  focusLinkEditFieldOnRender: boolean = false;

  readonly suggestions: SuggestionsModel;

  // Set by suggestion-select paths that need WrapInGroup to absorb adjacent brackets
  // even when the inline expression shouldn't be wrapped (eg. a single number node)
  forceWrapInGroup = false;

  textSelection: { from: number; to: number } = { from: -1, to: -1 };

  highlightedWrapperIds: Set<string> = new Set();

  highlightedVariableSymbols: Set<string> = new Set();

  /** Ids of expression wrappers and graphs inside locked blocks; kept in sync by the LockBlock extension. */
  lockedRootIds: Set<string> = new Set();

  readonly hints: PhantomHintsModel;

  isClipboardReadable = true;

  private disposeClipboardWatch: () => void;

  constructor(doc: DocumentModel) {
    this.doc = doc;
    makeAutoObservable(this);

    this.suggestions = new SuggestionsModel(this);
    this.hints = new PhantomHintsModel(this);
    this.disposeClipboardWatch = watchClipboardReadable((readable) =>
      runInAction(() => {
        this.isClipboardReadable = readable;
      }),
    );
  }

  dispose() {
    this.hints.dispose();
    this.disposeClipboardWatch();
  }

  updateEditorState = (newEditorState: EditorState): void => {
    Object.assign(this.editorState, newEditorState);
  };

  setLockedRootIds = (ids: Set<string>): void => {
    this.lockedRootIds = ids;
  };

  setIsLinkEditMode = (
    isLinkEditMode: boolean,
    options: { focusOnRender?: boolean } = {},
  ): void => {
    this.isLinkEditMode = isLinkEditMode;
    this.focusLinkEditFieldOnRender = !!options.focusOnRender;
    if (!this.isLinkEditMode) {
      this.focusLinkEditFieldOnRender = false;
    }
  };

  private get undoManager() {
    if (this.tiptap) {
      return getUndoManager(this.tiptap.state);
    }
  }

  /**
   * Prevents the following transaction to be merged with the previous one in the undo stack.
   */
  stopUndoCapturing() {
    this.undoManager?.stopCapturing();
  }

  clearUndo = (): void => {
    this.undoManager?.clear();
  };

  undo = (): void => {
    this.undoManager?.undo();
  };

  redo = (): void => {
    this.undoManager?.redo();
  };

  highlightFocusedDependencies() {
    const ids = new Set(this.doc.computedNodes.focusedDependencyWrapperIds);
    if (!isEqual(this.highlightedWrapperIds, ids)) {
      this.highlightedWrapperIds = ids;
    }
  }

  setHighlightedVariableSymbols(symbols: Set<string>) {
    if (isEqual(this.highlightedVariableSymbols, symbols)) {
      return;
    }
    this.highlightedVariableSymbols = symbols;
    this.tiptap?.commands.refreshVariableLinks?.();
  }

  resetInput(resetNode = false) {
    this.doc.input.reset(resetNode);
    this.suggestions.reset();
  }

  reset(focusTiptap = true) {
    this.doc.input.reset();
    this.suggestions.reset();
    this.doc.cursor.reset();
    if (focusTiptap) {
      this.tiptap?.chain().clearContent().focus().run();
    }
  }
}
